import React, { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  addJointApplicationData,
} from "app/lib/store/slices/steps/loanTypesSteps";

import { RootState } from "app/lib/store/store";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import SubTitle from "app/components/common/typography/SubTitle";
import InfoTitle from "app/components/common/typography/InfoTitle";

import { EmploymentDetailsForm } from "app/utils/applicant/types/types";
import EmploymentDynamicForm from "app/components/Individual/applicant/employment/EmploymentDynamicForm";

interface FormData {
  yourDetails: EmploymentDetailsForm;
  coBorrowerDetails: EmploymentDetailsForm;
}

const EmploymentDetailsPartOne: React.FC = () => {
  const dispatch = useDispatch();
  const applicationData = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    clearErrors,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      yourDetails: {
        employerName: "",
        employmentType: "",
        incomeFrequency: [
          { incomeType: "gross", amount: 0, frequency: "annually" },
          { incomeType: "other", amount: 0, frequency: "annually" },
        ],
      },
      coBorrowerDetails: {
        employerName: "",
        employmentType: "",
        incomeFrequency: [
          { incomeType: "gross", amount: 0, frequency: "annually" },
          { incomeType: "other", amount: 0, frequency: "annually" },
        ],
      },
    },
  });

  const yourEmployerType = watch("yourDetails.employmentType");
  const coEmployerType = watch("coBorrowerDetails.employmentType");

  const onSubmit = (data: FormData) => {
    const { coBorrowerDetails, yourDetails } = data;

    const {
      employerName: yourEmployerName,
      employmentType: yourEmploymentType,
      incomeFrequency: yourIncomeFrequency,
    } = yourDetails;
    const {
      employerName: coEmployerName,
      employmentType: coEmploymentType,
      incomeFrequency: coIncomeFrequency,
    } = coBorrowerDetails;

    const existingYourIncomes = applicationData?.YourDetails?.incomes || [];
    const existingCoIncomes = applicationData?.Coborrower?.incomes || [];

    const mapIncomeWithId = (
      items: typeof yourIncomeFrequency,
      existing: typeof existingYourIncomes
    ) =>
      (items || []).map((item) => {
        const existingIncome = existing.find(
          (i: any) => i.type === item.incomeType
        );

        const base = {
          type: item.incomeType,
          amount: Number(item.amount),
          frequency: item.frequency,
        };

        return existingIncome?.id ? { ...base, id: existingIncome.id } : base;
      });

    const coBorrowerDetailsData = {
      ...applicationData.Coborrower,
      employment: {
        ...applicationData.Coborrower.employment,
        employerName: coEmployerName,
        employmentType: coEmploymentType,
      },
      // incomes: [
      //   ...(coIncomeFrequency?.map((item, index) => ({
      //     type: item.incomeType,
      //     amount: Number(item.amount),
      //     frequency: item.frequency,
      //   })) || []),
      // ],
      incomes: mapIncomeWithId(coIncomeFrequency, existingCoIncomes),
    };

    const yourDetailsData = {
      ...applicationData.YourDetails,
      employment: {
        ...applicationData.YourDetails.employment,
        employerName: yourEmployerName,
        employmentType: yourEmploymentType,
      },
      // incomes: [
      //   ...(yourIncomeFrequency?.map((item, index) => ({
      //     type: item.incomeType,
      //     amount: Number(item.amount),
      //     frequency: item.frequency,
      //   })) || []),
      // ],
      incomes: mapIncomeWithId(yourIncomeFrequency, existingYourIncomes),
    };

    dispatch(
      addJointApplicationData({
        category: "YourDetails",
        data: yourDetailsData,
      })
    );
    dispatch(
      addJointApplicationData({
        category: "Coborrower",
        data: coBorrowerDetailsData,
      })
    );
    dispatch(nextStep());
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "yourDetails.incomeFrequency",
  });

  const {
    fields: coFields,
    append: coAppend,
    remove: Coremove,
  } = useFieldArray({
    control,
    name: "coBorrowerDetails.incomeFrequency",
  });

  useEffect(() => {
    if (applicationData?.YourDetails && applicationData?.Coborrower) {
      reset({
        yourDetails: {
          employerName:
            applicationData.YourDetails?.employment?.employerName ?? "",
          employmentType:
            applicationData.YourDetails?.employment?.employmentType ?? "",
          incomeFrequency:
            Array.isArray(applicationData.YourDetails?.incomes) &&
            applicationData.YourDetails.incomes.length > 0
              ? applicationData.YourDetails.incomes.map((item: any) => ({
                  incomeType: item?.type ?? "",
                  amount:
                    item?.amount !== undefined
                      ? Number(item.amount)
                      : undefined,
                  frequency: item?.frequency ?? "",
                }))
              : [
                  {
                    incomeType: "gross",
                    amount: 0,
                    frequency: "annually",
                  },
                  { incomeType: "other", amount: 0, frequency: "annually" },
                ],
        },
        coBorrowerDetails: {
          employerName:
            applicationData.Coborrower?.employment?.employerName ?? "",
          employmentType:
            applicationData.Coborrower?.employment?.employmentType ?? "",
          incomeFrequency:
            Array.isArray(applicationData.Coborrower?.incomes) &&
            applicationData.Coborrower.incomes.length > 0
              ? applicationData.Coborrower.incomes.map((item: any) => ({
                  incomeType: item?.type ?? "",
                  amount:
                    item?.amount !== undefined
                      ? Number(item.amount)
                      : undefined,
                  frequency: item?.frequency ?? "",
                }))
              : [
                  {
                    incomeType: "gross",
                    amount: 0,
                    frequency: "annually",
                  },
                  { incomeType: "other", amount: 0, frequency: "annually" },
                ],
        },
      });
    }
  }, [applicationData, reset]);

  useEffect(() => {
    if (yourEmployerType === "unemployed_or_retired") {
      clearErrors("yourDetails.employerName");
      clearErrors("yourDetails.incomeFrequency");
      fields.forEach((field, index) => {
        setValue(`yourDetails.incomeFrequency.${index}.amount`, 0);
      });
    } else if (coEmployerType === "unemployed_or_retired") {
      coFields.forEach((field, index) => {
        setValue(`coBorrowerDetails.incomeFrequency.${index}.amount`, 0);
      });
      clearErrors("coBorrowerDetails.employerName");
      clearErrors("coBorrowerDetails.incomeFrequency");
    }
  }, [yourEmployerType, coEmployerType]);

  return (
    <>
      <ProgressBar progress={80} />
      <div className="w-full  sm:w-[70%] xl:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Employment Details" />
        <SubTitle title="Please enter your employment details below" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-5 flex-col md:flex-col xl:flex-row gap-3 sm:gap-4 ">
            <div className="flex-1">
              <InfoTitle title="Your Details" />
              <EmploymentDynamicForm
                control={control}
                errors={errors}
                fields={fields}
                employmentTypeName="yourDetails.employmentType"
                employerName="yourDetails.employerName"
                incomeName="yourDetails.incomeFrequency"
                employmenttypeValue={watch("yourDetails.employmentType")}
              />
            </div>
            <div className="flex-1">
              <InfoTitle title="Co-borrower" />
              <EmploymentDynamicForm
                control={control}
                errors={errors}
                fields={coFields}
                employmentTypeName="coBorrowerDetails.employmentType"
                employerName="coBorrowerDetails.employerName"
                incomeName="coBorrowerDetails.incomeFrequency"
                employmenttypeValue={watch("coBorrowerDetails.employmentType")}
              />
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton type="submit">Next</ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default EmploymentDetailsPartOne;
